import { useState } from 'react';
import type { Action, OnboardingState, Proche } from './state.js';

interface Props {
  state: OnboardingState;
  dispatch: React.Dispatch<Action>;
  onNext: () => void;
  onBack: () => void;
}

const EMPTY_PROCHE: Proche = { prenom: '', contact: '', role: 'proche' };

export function Step4Proches({ state, dispatch, onNext }: Props) {
  const [proches, setProches] = useState<Proche[]>(
    state.proches.length > 0 ? state.proches : [{ ...EMPTY_PROCHE }],
  );

  function update(i: number, field: keyof Proche, value: string) {
    setProches((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));
  }

  function add() {
    setProches((prev) => [...prev, { ...EMPTY_PROCHE }]);
  }

  function remove(i: number) {
    setProches((prev) => prev.filter((_, idx) => idx !== i));
  }

  const valid = proches.filter((p) => p.prenom.trim() && p.contact.trim());

  function handleNext() {
    dispatch({ type: 'SET_PROCHES', proches: valid });
    onNext();
  }

  return (
    <div
      className="container"
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'start' }}
    >
      <div style={{ position: 'sticky', top: '160px' }}>
        <div className="eyebrow" style={{ marginBottom: '12px' }}>
          Étape 3 — Famille
        </div>
        <h1
          className="display"
          style={{ fontSize: 'clamp(42px, 4.5vw, 64px)', margin: '0 0 20px' }}
        >
          Invitez <em>les proches</em>.
        </h1>
        <p
          className="serif"
          style={{
            fontStyle: 'italic',
            fontWeight: 300,
            fontSize: '18px',
            lineHeight: 1.4,
            color: 'var(--ink-soft)',
          }}
        >
          Ils recevront un e-mail pour rejoindre le foyer depuis leur téléphone.
        </p>
        {valid.length > 0 && (
          <div
            style={{
              marginTop: '24px',
              padding: '12px 16px',
              background: 'var(--moss-soft)',
              border: '1px solid var(--moss)',
              borderRadius: '8px',
            }}
          >
            <span className="small" style={{ color: 'var(--moss)' }}>
              {valid.length} proche{valid.length > 1 ? 's' : ''} prêt{valid.length > 1 ? 's' : ''} à
              inviter
            </span>
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'grid', gap: '16px', marginBottom: '20px' }}>
          {proches.map((p, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: liste ordonnée mutable, index stable
              key={i}
              style={{
                background: 'var(--paper-2)',
                border: '1px solid var(--hairline-soft)',
                borderRadius: '10px',
                padding: '18px',
                display: 'grid',
                gap: '10px',
              }}
            >
              <div className="field-row">
                <div className="field">
                  <label htmlFor={`prenom-${i}`}>Prénom</label>
                  <input
                    id={`prenom-${i}`}
                    type="text"
                    value={p.prenom}
                    onChange={(e) => update(i, 'prenom', e.target.value)}
                    placeholder="Julie"
                  />
                </div>
                <div className="field">
                  <label htmlFor={`contact-${i}`}>E-mail ou tél.</label>
                  <input
                    id={`contact-${i}`}
                    type="text"
                    value={p.contact}
                    onChange={(e) => update(i, 'contact', e.target.value)}
                    placeholder="julie@exemple.fr"
                  />
                </div>
              </div>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <select
                  value={p.role}
                  onChange={(e) => update(i, 'role', e.target.value)}
                  style={{
                    fontSize: '13px',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid var(--hairline)',
                    background: 'var(--paper)',
                    color: 'var(--ink)',
                  }}
                >
                  <option value="proche">Proche</option>
                  <option value="co-admin">Co-administrateur</option>
                </select>
                {proches.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    style={{
                      fontSize: '12px',
                      color: 'var(--critique)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 8px',
                    }}
                  >
                    Retirer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={add}
          style={{
            width: '100%',
            padding: '12px',
            border: '1px dashed var(--hairline)',
            borderRadius: '10px',
            background: 'none',
            color: 'var(--ink-soft)',
            cursor: 'pointer',
            fontSize: '14px',
            marginBottom: '24px',
          }}
        >
          + Ajouter un proche
        </button>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button type="button" className="btn btn-secondary" onClick={onNext} style={{ flex: 1 }}>
            Passer
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleNext}
            style={{ flex: 1 }}
          >
            Inviter {valid.length > 0 ? `(${valid.length})` : ''} & continuer
          </button>
        </div>
      </div>
    </div>
  );
}
